import { cn } from '@/lib/utils';

import BotaoComecar from './BotaoComecar';
import SeletorLocais, { usePrecoLocais } from './SeletorLocais';

/**
 * La barra pegada abajo en TODO el scroll, en móvil y en ordenador: precio,
 * vagas que quedan y el botón siempre a mano. La página reserva su alto con un
 * `padding-bottom` para que no tape el pie.
 *
 * En móviles estrechos (320–375 px) no cabe todo en una línea, y medido así
 * el botón se quedaba sus 186 px y al texto le tocaba lo que sobraba: a 320 px
 * se leía «Restam 43 …», o sea, se perdía justo la cifra. Se reparte al revés —
 * lo que importa nunca se corta y lo que sobra desaparece por anchos:
 *
 *   - «Restam 43 vagas» siempre entero; «de 50» solo desde 400 px, y por
 *     debajo de 300 px (Galaxy Fold cerrado) se queda en «43 vagas».
 *   - el 621,70 € tachado solo desde 380 px; el 287 € siempre.
 *   - el botón se compacta (padding y letra) en dos escalones, 340 y 380 px,
 *     pero su texto nunca se trunca: `shrink-0` + `whitespace-nowrap`.
 *
 * Medido con la página en iframes de 280 a 414 px (Galaxy Fold cerrado,
 * iPhone SE 1.ª, Android pequeños, iPhone mini…): ni la fila desborda ni se
 * corta ningún texto.
 *
 * Lleva además el selector de locales (`seletor`) y el precio pasa a ser el de
 * los N elegidos, con su «valor total» tachado. No cabe todo en una fila por debajo
 * de 640 px (`sm`), así que ahí el botón baja a su propia fila a todo el ancho
 * y la página reserva más alto (`OfertaPtV4Vista`). Medido: a 414 px en una
 * fila el selector pisaba el precio.
 *
 * @param {{ restantes: number|null, total: number,
 *           comecar: (id: string) => void, cargando: string|null,
 *           seletor?: { locais: number, setLocais: (n: number) => void } }} props
 */
export default function BarraFixa({ restantes, total, comecar, cargando, seletor }) {
  const preco = usePrecoLocais(seletor?.locais || 1);
  const editavel = !!seletor;
  return (
    <div
      data-testid="oferta-pt-barra"
      className="fixed inset-x-0 bottom-0 z-40 border-t border-[#ececf0] bg-white/95 backdrop-blur-md"
      style={{ paddingBottom: 'calc(12px + env(safe-area-inset-bottom))' }}
    >
      <div className={cn(
        'mx-auto flex max-w-[1160px] items-center justify-between gap-2 px-3 pt-3 min-[360px]:gap-3 min-[360px]:px-4 md:px-10',
        editavel && 'flex-wrap gap-y-2.5 sm:flex-nowrap',
      )}
      >
        <div className="flex min-w-0 flex-col gap-px">
          <div className="flex items-baseline gap-1.5 whitespace-nowrap">
            <span className="hidden text-xs font-medium text-[#6e6e76] line-through min-[380px]:inline">{preco.riscado}</span>
            <span className="text-lg font-extrabold tracking-tight text-[#1d1d1f]">{preco.total}</span>
            <span className="text-xs font-semibold text-[#5e5e66]">/ano</span>
          </div>
          <span className="whitespace-nowrap text-xs font-semibold text-[#9a5700]">
            {restantes == null ? (
              `Só ${total} vagas`
            ) : (
              <>
                <span className="hidden min-[300px]:inline">Restam </span>
                {restantes}
                <span className="hidden min-[400px]:inline"> de {total}</span>
                {' '}vagas
              </>
            )}
          </span>
        </div>
        {editavel && <SeletorLocais {...seletor} compacto className="sm:ml-auto" />}
        <BotaoComecar
          cta="landing.oferta-pt.comprar-barra"
          comecar={comecar}
          cargando={cargando}
          className={cn(
            'min-h-[48px] shrink-0 whitespace-nowrap rounded-xl px-2.5 text-[13px] shadow-none min-[340px]:px-3 min-[340px]:text-sm min-[380px]:min-h-[50px] min-[380px]:px-4 min-[380px]:text-[15px]',
            editavel && 'basis-full sm:basis-auto',
          )}
        />
      </div>
    </div>
  );
}
